import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { apiErr, mediaUrl } from "@/lib/api";
import { MicButton } from "@/components/MicButton";
import { Container, PageHeader } from "@/components/shared";
import { Avatar } from "@/pages/MemberProfile";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus, X, Heart, MessageCircle, Flag, Bell, BellRing, Loader2, Tag, MapPin, Sparkles, Send, Star, Flame, CalendarDays,
} from "lucide-react";

const TYPE_LABEL = { discussion: "Discussion", bon_plan: "Bon plan" };
const CAT_LABEL = {
  restaurant: "Restaurant", librairie: "Librairie", association: "Association", coiffure: "Coiffure",
  evenement: "Événement", festival: "Festival", exposition: "Exposition", musee: "Musée",
  coworking_business: "Business", entrepreneur: "Entrepreneur", artiste: "Artiste",
  tourisme: "Tourisme", association_etudiante: "Asso étudiante", commerce: "Commerce", autre: "Autre",
};

const AuthorLink = ({ id, name, avatar, size = 22 }) => {
  const inner = (
    <span className="inline-flex items-center gap-1.5">
      <Avatar src={avatar} name={name} size={size} />
      <span className="hover:text-gold transition-colors">{name}</span>
    </span>
  );
  return id ? <Link to={`/membre/${id}`} data-testid={`author-${id}`} onClick={(e) => e.stopPropagation()} className="text-inherit">{inner}</Link> : inner;
};

export default function EspaceCommunautaire() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [meta, setMeta] = useState({ categories: [], can_post: true });
  const [scope, setScope] = useState({ type: "", value: "" }); // '' = fil général
  const [typeFilter, setTypeFilter] = useState("");
  const [q, setQ] = useState("");
  const [posts, setPosts] = useState([]);
  const [openPost, setOpenPost] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [follows, setFollows] = useState([]);
  const [highlights, setHighlights] = useState(null);
  const [recos, setRecos] = useState([]);

  const refreshFollows = useCallback(() => {
    api.get("/community-space/follows").then(({ data }) => setFollows(data.follows || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/community-space/meta").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/discovery/highlights").then(({ data }) => setHighlights(data)).catch(() => {});
    api.get("/discovery/recommendations").then(({ data }) => setRecos(data.places || [])).catch(() => {});
    refreshFollows();
  }, [refreshFollows]);

  const load = useCallback(async () => {
    try {
      const params = { sort: "recent" };
      if (scope.type && scope.value) { params.scope_type = scope.type; params.scope_value = scope.value; }
      if (typeFilter) params.type = typeFilter;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/community-space/posts", { params });
      setPosts(data.posts || []);
    } catch (e) { /* silencieux */ }
  }, [scope, typeFilter, q]);
  useEffect(() => { load(); }, [load]);

  // ouverture directe via ?post=
  useEffect(() => {
    const pid = sp.get("post");
    if (pid) api.get(`/community-space/posts/${pid}`).then(({ data }) => setOpenPost(data)).catch(() => {});
  }, [sp]);

  const isFollowing = (t, v) => follows.some((f) => f.target_type === t && f.target_value === v);
  const toggleFollow = async (t, v) => {
    try { await api.post("/community-space/follow", { target_type: t, target_value: v }); refreshFollows(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Container>
      <PageHeader title={<>Espace <span className="gold-text">Communautaire</span></>}
        subtitle="Discussions par ville et par thème, bons plans, entraide — entre membres de la communauté." />

      {highlights && <Highlights data={highlights} recos={recos} onOpenPost={(id) => api.get(`/community-space/posts/${id}`).then(({ data }) => setOpenPost(data)).catch(() => {})} categories={meta.categories} />}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Colonne canaux */}
        <aside className="lg:w-64 shrink-0 space-y-4">
          <button data-testid="cs-new-post" onClick={() => (meta.can_post ? setShowNew(true) : toast.error("Publication indisponible pour votre compte."))}
            className="w-full inline-flex items-center justify-center gap-2 gold-gradient text-black rounded-full py-2.5 font-medium">
            <Plus size={17} /> Nouveau sujet
          </button>
          {!meta.can_post && <p className="text-red-300 text-xs">{meta.banned ? "Compte banni de l'espace." : `Suspendu jusqu'au ${(meta.suspended_until||'').slice(0,10)}.`}</p>}

          <div className="card-surface p-3">
            <p className="text-white/40 text-[11px] uppercase tracking-widest mb-2">Fils</p>
            <ChannelBtn active={!scope.type} onClick={() => setScope({ type: "", value: "" })} label="Fil général" icon={Sparkles} />
          </div>

          <div className="card-surface p-3">
            <p className="text-white/40 text-[11px] uppercase tracking-widest mb-2">Par thème</p>
            <div className="space-y-0.5">
              {meta.categories.map((c) => (
                <ChannelBtn key={c.key} active={scope.type === "category" && scope.value === c.key} icon={Tag}
                  onClick={() => setScope({ type: "category", value: c.key })} label={c.label}
                  following={isFollowing("category", c.key)} onFollow={() => toggleFollow("category", c.key)} />
              ))}
            </div>
          </div>

          <div className="card-surface p-3">
            <p className="text-white/40 text-[11px] uppercase tracking-widest mb-2">Par ville</p>
            <input data-testid="cs-city-input" placeholder="Ex. Paris, Marseille…"
              onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) setScope({ type: "city", value: e.target.value.trim() }); }}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-gold/40" />
            {scope.type === "city" && (
              <div className="flex items-center justify-between mt-2 text-sm">
                <span className="flex items-center gap-1 text-gold"><MapPin size={13} /> {scope.value}</span>
                <button onClick={() => toggleFollow("city", scope.value)} className="text-xs text-white/50 hover:text-gold">
                  {isFollowing("city", scope.value) ? "Suivi ✓" : "Suivre"}
                </button>
              </div>
            )}
          </div>
        </aside>

        {/* Feed */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-4">
            <input data-testid="cs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…"
              className="flex-1 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-gold/40" />
            <select data-testid="cs-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="bg-[#0c0c0c] border border-white/15 rounded-full text-sm px-3 py-2 text-white/80 outline-none">
              <option value="">Tout</option>
              <option value="discussion">Discussions</option>
              <option value="bon_plan">Bons plans</option>
            </select>
          </div>

          <div className="space-y-3">
            {posts.length === 0 && <p className="text-white/30 text-sm py-8 text-center">Aucun sujet ici pour l'instant. Lancez la discussion !</p>}
            {posts.map((p) => (
              <button key={p.post_id} data-testid={`cs-post-${p.post_id}`} onClick={() => setOpenPost(p)}
                className="w-full text-left card-surface p-4 hover:border-gold/30 transition-colors">
                <div className="flex items-center gap-2 mb-1">
                  {p.pinned && <span className="text-gold text-xs">📌</span>}
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${p.type === "bon_plan" ? "bg-gold/15 text-gold" : "border border-white/15 text-white/50"}`}>{TYPE_LABEL[p.type]}</span>
                  <span className="text-white/40 text-xs">{meta.categories.find((c)=>c.key===p.category)?.label || p.category}{p.city ? ` · ${p.city}` : ""}</span>
                </div>
                <p className="font-medium">{p.title}</p>
                {p.body && <p className="text-white/55 text-sm mt-1 line-clamp-2">{p.body}</p>}
                <div className="flex items-center gap-4 mt-2 text-white/40 text-xs">
                  <span className="flex items-center gap-1"><Heart size={13} /> {p.like_count}</span>
                  <span className="flex items-center gap-1"><MessageCircle size={13} /> {p.comment_count}</span>
                  <span className="flex items-center gap-1">par <AuthorLink id={p.author_id} name={p.author_name} avatar={p.author_avatar} size={18} /></span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {openPost && <PostDetail postId={openPost.post_id} user={user} onClose={() => { setOpenPost(null); if (sp.get("post")) { sp.delete("post"); setSp(sp); } load(); }}
        isFollowing={isFollowing} toggleFollow={toggleFollow} categories={meta.categories} />}
      {showNew && <NewPostModal categories={meta.categories} onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); load(); }} />}
    </Container>
  );
}

const HScroll = ({ children }) => (
  <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 no-scrollbar snap-x">{children}</div>
);

function Highlights({ data, recos = [], onOpenPost, categories }) {
  const { featured_places = [], events = [], discussions = [] } = data;
  if (!featured_places.length && !events.length && !discussions.length && !recos.length) return null;
  const catLabel = (k) => categories.find((c) => c.key === k)?.label || k;

  const PlaceCard = ({ p, testid, icon: Icon = MapPin }) => (
    <Link to={`/carte-afro?place=${p.place_id}`} data-testid={testid}
      className="snap-start shrink-0 w-56 card-surface overflow-hidden hover:border-gold/40 transition-colors">
      {p.photo ? <img src={mediaUrl(p.photo)} alt="" className="h-28 w-full object-cover" />
        : <div className="h-28 w-full bg-white/[0.04] flex items-center justify-center"><Icon size={22} className="text-gold/50" /></div>}
      <div className="p-3">
        <span className="text-[10px] uppercase tracking-wide text-gold">{CAT_LABEL[p.category] || p.category}</span>
        <p className="font-medium text-sm truncate mt-0.5">{p.name}</p>
        <p className="text-white/40 text-xs mt-0.5">{p.city}{p.rating ? ` · ★ ${p.rating}` : ""}</p>
      </div>
    </Link>
  );

  return (
    <div className="mb-8 space-y-6" data-testid="cs-highlights">
      {recos.length > 0 && (
        <section data-testid="cs-recommendations">
          <h2 className="font-display text-lg mb-3 flex items-center gap-2"><Sparkles size={16} className="text-gold" /> Recommandé pour vous</h2>
          <HScroll>{recos.map((p) => <PlaceCard key={p.place_id} p={p} testid={`reco-place-${p.place_id}`} />)}</HScroll>
        </section>
      )}

      {featured_places.length > 0 && (
        <section>
          <h2 className="font-display text-lg mb-3 flex items-center gap-2"><Star size={16} className="text-gold" /> Lieux à la une</h2>
          <HScroll>{featured_places.map((p) => <PlaceCard key={p.place_id} p={p} testid={`hl-place-${p.place_id}`} />)}</HScroll>
        </section>
      )}

      {events.length > 0 && (
        <section>
          <h2 className="font-display text-lg mb-3 flex items-center gap-2"><CalendarDays size={16} className="text-gold" /> Événements & expositions</h2>
          <HScroll>{events.map((p) => <PlaceCard key={p.place_id} p={p} testid={`hl-event-${p.place_id}`} icon={CalendarDays} />)}</HScroll>
        </section>
      )}

      {discussions.length > 0 && (
        <section>
          <h2 className="font-display text-lg mb-3 flex items-center gap-2"><Flame size={16} className="text-gold" /> Discussions à la une</h2>
          <HScroll>
            {discussions.map((d) => (
              <button key={d.post_id} onClick={() => onOpenPost(d.post_id)} data-testid={`hl-disc-${d.post_id}`}
                className="snap-start shrink-0 w-64 text-left card-surface p-4 hover:border-gold/40 transition-colors">
                <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${d.type === "bon_plan" ? "bg-gold/15 text-gold" : "border border-white/15 text-white/50"}`}>{TYPE_LABEL[d.type]}</span>
                <p className="font-medium text-sm mt-2 line-clamp-2">{d.title}</p>
                <div className="flex items-center gap-3 mt-2 text-white/40 text-xs">
                  <span className="flex items-center gap-1"><Heart size={12} /> {d.like_count}</span>
                  <span className="flex items-center gap-1"><MessageCircle size={12} /> {d.comment_count}</span>
                  <span className="truncate">{catLabel(d.category)}</span>
                </div>
              </button>
            ))}
          </HScroll>
        </section>
      )}
    </div>
  );
}

const ChannelBtn = ({ active, onClick, label, icon: Icon, following, onFollow }) => (
  <div className={`flex items-center justify-between rounded-lg px-2.5 py-1.5 text-sm ${active ? "bg-white/[0.06] text-white" : "text-white/60 hover:text-white"}`}>
    <button onClick={onClick} className="flex items-center gap-2 flex-1 text-left">{Icon && <Icon size={14} className={active ? "text-gold" : ""} />} {label}</button>
    {onFollow && <button onClick={onFollow} title="Suivre" className={`shrink-0 ${following ? "text-gold" : "text-white/25 hover:text-gold"}`}>{following ? <BellRing size={13} /> : <Bell size={13} />}</button>}
  </div>
);

function PostDetail({ postId, user, onClose, isFollowing, toggleFollow, categories }) {
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        api.get(`/community-space/posts/${postId}`),
        api.get(`/community-space/posts/${postId}/comments`),
      ]);
      setPost(p.data); setComments(c.data.comments || []);
    } catch (e) { toast.error(apiErr(e)); onClose(); }
  }, [postId]);
  useEffect(() => { load(); }, [load]);

  const like = async () => { try { const { data } = await api.post(`/community-space/posts/${postId}/like`); setPost((p) => ({ ...p, liked: data.liked, like_count: p.like_count + (data.liked ? 1 : -1) })); } catch (e) { toast.error(apiErr(e)); } };
  const report = async () => { const reason = window.prompt("Signaler ce sujet — motif :") ?? ""; try { await api.post(`/community-space/posts/${postId}/report`, { reason }); toast.success("Signalé. Merci."); } catch (e) { toast.error(apiErr(e)); } };
  const reportC = async (id) => { const reason = window.prompt("Signaler ce commentaire — motif :") ?? ""; try { await api.post(`/community-space/comments/${id}/report`, { reason }); toast.success("Signalé."); } catch (e) { toast.error(apiErr(e)); } };
  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/community-space/posts/${postId}/comments`, { body });
      setComments((prev) => [...prev, data.comment]);
      setPost((p) => (p ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p));
      setText("");
      toast.success("Réponse publiée.");
    } catch (e) {
      toast.error(apiErr(e) || "Échec de la publication. Votre texte est conservé, réessayez.");
    } finally { setBusy(false); }
  };

  if (!post) return null;
  return (
    <div className="fixed inset-0 z-[700] flex items-end lg:items-center justify-center p-0 lg:p-6" data-testid="cs-post-detail">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full lg:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl lg:rounded-3xl border border-gold/25 p-6" style={{ background: "#0A0A0A" }}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white" data-testid="cs-detail-close"><X size={22} /></button>
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${post.type === "bon_plan" ? "bg-gold/15 text-gold" : "border border-white/15 text-white/50"}`}>{TYPE_LABEL[post.type]}</span>
          <span className="text-white/40 text-xs">{categories.find((c)=>c.key===post.category)?.label || post.category}{post.city ? ` · ${post.city}` : ""}</span>
        </div>
        <h2 className="font-display text-xl mb-1">{post.title}</h2>
        <p className="text-white/40 text-xs mb-3 flex items-center gap-1">par <AuthorLink id={post.author_id} name={post.author_name} avatar={post.author_avatar} size={20} /></p>
        {post.body && <p className="text-white/75 text-sm whitespace-pre-wrap mb-4">{post.body}</p>}
        <div className="flex items-center gap-2 mb-5">
          <button onClick={like} data-testid="cs-like" className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${post.liked ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/70"}`}><Heart size={14} fill={post.liked ? "currentColor" : "none"} /> {post.like_count}</button>
          <button onClick={() => toggleFollow("discussion", postId)} data-testid="cs-follow-discussion" className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${isFollowing("discussion", postId) ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/70"}`}>{isFollowing("discussion", postId) ? <BellRing size={14} /> : <Bell size={14} />} Suivre</button>
          <button onClick={report} data-testid="cs-report-post" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 text-white/50 hover:text-red-400 px-3 py-1.5 text-sm"><Flag size={14} /> Signaler</button>
        </div>

        <h3 className="font-medium text-sm mb-3 text-white/70">{comments.length} réponse(s)</h3>
        <div className="space-y-3 mb-4">
          {comments.map((c) => (
            <div key={c.comment_id} className="border-b border-white/5 pb-2" data-testid={`cs-comment-${c.comment_id}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium"><AuthorLink id={c.author_id} name={c.author_name} avatar={c.author_avatar} size={20} /></span>
                <button onClick={() => reportC(c.comment_id)} className="text-white/25 hover:text-red-400" title="Signaler"><Flag size={12} /></button>
              </div>
              <p className="text-white/65 text-sm mt-0.5 whitespace-pre-wrap">{c.body}</p>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <textarea data-testid="cs-comment-input" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Votre réponse…"
            className="flex-1 bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-gold/40" />
          <MicButton testid="cs-comment-mic" value={text} onChange={setText} />
          <button onClick={send} disabled={busy} data-testid="cs-comment-send" className="gold-gradient text-black rounded-full p-2.5 disabled:opacity-50">{busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
        </div>
      </div>
    </div>
  );
}

function NewPostModal({ categories, onClose, onDone }) {
  const [form, setForm] = useState({ type: "discussion", title: "", body: "", category: "general", city: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) return toast.error("Ajoutez un titre.");
    setBusy(true);
    try { await api.post("/community-space/posts", form); toast.success("Sujet publié !"); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const inp = "w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-gold/40";
  return (
    <div className="fixed inset-0 z-[700] flex items-end lg:items-center justify-center p-0 lg:p-6" data-testid="cs-new-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full lg:max-w-lg rounded-t-3xl lg:rounded-3xl border border-gold/25 p-6" style={{ background: "#0A0A0A" }}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={22} /></button>
        <h2 className="font-display text-xl mb-4">Nouveau sujet</h2>
        <div className="space-y-3">
          <div className="flex gap-2">
            {["discussion", "bon_plan"].map((t) => (
              <button key={t} onClick={() => setForm({ ...form, type: t })} data-testid={`cs-new-type-${t}`}
                className={`flex-1 rounded-full py-2 text-sm border ${form.type === t ? "gold-gradient text-black border-transparent" : "border-white/15 text-white/60"}`}>{TYPE_LABEL[t]}</button>
            ))}
          </div>
          <input data-testid="cs-new-title" placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inp} />
          <textarea data-testid="cs-new-body" rows={4} placeholder="Votre message…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={inp} />
          <div><MicButton testid="cs-new-body-mic" value={form.body} onChange={(v) => setForm((f) => ({ ...f, body: v }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="cs-new-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inp}>
              {categories.map((c) => <option key={c.key} value={c.key} className="bg-[#0A0A0A]">{c.label}</option>)}
            </select>
            <input placeholder="Ville (optionnel)" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inp} />
          </div>
        </div>
        <button onClick={submit} disabled={busy} data-testid="cs-new-submit" className="w-full mt-5 inline-flex items-center justify-center gap-2 gold-gradient text-black rounded-full py-3 font-medium disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Publier
        </button>
      </div>
    </div>
  );
}
